export const GUIDES = {
  "track-chain-wear-guide": {
    title: "Track Chain Wear Guide: How to Measure & When to Replace",
    metaTitle:
      "Track Chain Wear: How to Measure Pitch & When to Replace — Expert Guide",
    desc: "Learn how to measure track chain wear (pitch elongation, bushing OD, link height), the wear limits that matter, and when replacing the chain saves you money. By VANROCK undercarriage engineers.",
    minutes: 7,
    sections: [
      {
        h: "Why track chain wear matters more than any other component",
        ps: [
          "The track chain sets the pitch for the entire undercarriage. When pins and bushings wear, the chain 'grows' — and a stretched chain no longer meshes cleanly with the sprocket. Every tooth engagement then hammers the bushings, wears the sprocket into hooks, and accelerates roller and idler tread wear. That is why undercarriage professionals measure chain wear first: it predicts the health of everything else.",
        ],
      },
      {
        h: "How to measure pitch elongation (the #1 wear indicator)",
        ps: [
          "Tension the track so the chain is tight over the top rollers. Measure across 4–5 link pitches from pin center to pin center with a tape or pitch gauge, then divide by the number of pitches and compare against the nominal pitch for your chain (listed on every VANROCK track chain page).",
          "Rule of thumb: at 3% elongation a sealed chain is due for inspection; at 5% (or 100% of the OEM wear limit) the internal bushing wear is consumed and the chain should be replaced before it destroys the sprocket. Dry (non-lubricated) chains wear internally much faster and should be checked twice as often.",
        ],
      },
      {
        h: "Bushing outside diameter and link height",
        ps: [
          "Bushing OD wear happens on the outside, where the sprocket engages — measure with calipers at the most polished spot. Link height (rail height) wears against the rollers; measure from the link bottom to the rail surface. Both dimensions have published OEM wear limits; as a general guide, 100% worn means the hardened layer is gone and wear then accelerates 3–5× through the soft core.",
        ],
      },
      {
        h: "Sealed & lubricated (SALT) vs dry chains",
        ps: [
          "Sealed and lubricated chains hold oil inside the pin–bushing joint, so internal wear (pitch growth) is dramatically slower — they typically reach the external wear limits first. Dry chains rely on hardness alone and elongate faster, especially in abrasive, wet or sandy ground. All VANROCK excavator track chains use SALT construction with induction-hardened pins and bushings.",
        ],
      },
      {
        h: "When to replace: chain only, or chain + sprocket?",
        ps: [
          "Never run a new chain on a hooked sprocket — the worn tooth profile will wear the new bushings in a few hundred hours. If the sprocket shows hooking or sharpened tips, replace it (or its segment group) together with the chain. Rollers and idlers can usually stay if their treads measure under 50% wear.",
          "If your chain measures over the limit, get a manufacturer price for your exact model — VANROCK stocks track chains for 9,000+ machine models.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is the wear limit for a track chain?",
        a: "Most OEMs allow around 3 mm of pitch elongation per 100 mm of nominal pitch (≈3%) before recommending replacement, and consider the chain fully worn at ~5%. Exact limits vary by machine — send us your model and we'll confirm the numbers.",
      },
      {
        q: "How long does a track chain last?",
        a: "In average conditions 2,000–4,000 hours for sealed and lubricated chains on excavators; abrasive or high-speed applications can halve that. Correct track tension is the single biggest factor an operator controls.",
      },
      {
        q: "Can I replace just one side?",
        a: "Yes, chains are replaced per side — but measure both. If both sides are near the limit, replacing together avoids a second machine-down event weeks later.",
      },
    ],
    related: [
      { name: "Track chains by brand", path: "/parts/track-chain" },
      { name: "Sprockets & segments", path: "/parts/sprocket" },
      {
        name: "Undercarriage inspection checklist",
        path: "/guides/undercarriage-inspection-checklist",
      },
    ],
  },
  "undercarriage-inspection-checklist": {
    title: "Excavator Undercarriage Inspection: The 10-Point Checklist",
    metaTitle:
      "Excavator Undercarriage Inspection Checklist — 10 Points, Wear Limits & Tips",
    desc: "A practical 10-point undercarriage inspection checklist for excavators and dozers: what to measure on chains, rollers, idlers, sprockets and shoes, with wear signs that predict failures.",
    minutes: 6,
    sections: [
      {
        h: "Why inspect on a schedule",
        ps: [
          "Undercarriage is typically 50% of an excavator's lifetime repair cost. A 20-minute inspection every 250 hours catches wear while you still have choices — replace one component now, or plan a full undercarriage job at the next service window instead of an emergency field failure.",
        ],
      },
      {
        h: "The 10-point checklist",
        ps: [
          "1. Track tension — check sag against the OEM spec; over-tension is the #1 avoidable wear accelerator. 2. Track chain pitch — measure elongation over 4 pitches (see our track chain wear guide). 3. Bushing OD — look for polishing and measure at the sprocket contact point. 4. Link/rail height — measure and compare to the wear limit. 5. Track shoes — check grouser height and look for bent or cracked plates and loose hardware.",
          "6. Bottom rollers — measure tread diameter, check for flat spots, leaks and seized rollers. 7. Carrier rollers — spin by hand where safe; a dry, noisy or seized top roller drags the chain. 8. Front idler — measure tread, check flange wear and look for oil weep at the seal. 9. Sprocket / segments — inspect tooth profile for sharpening and hooking. 10. Frame & hardware — guards, bolts, recoil spring condition, and evidence of packing (mud/rock buildup).",
        ],
      },
      {
        h: "Reading the wear pattern",
        ps: [
          "Uneven wear tells a story: flange wear on one side of the rollers usually means misalignment or worn idler guides; scalloped roller treads point to a seized roller upstream; rapid bushing wear with normal link wear indicates over-tension or dry-joint failure. Photograph measurements each service so the trend, not just the number, drives your decision.",
        ],
      },
      {
        h: "Turning inspection into a parts plan",
        ps: [
          "Rank each component by % worn. Anything above 80% goes on the next order; 50–80% gets budgeted; below 50% is monitored. VANROCK can quote a complete undercarriage kit or individual components for your exact model — direct from the manufacturer.",
        ],
      },
    ],
    faqs: [
      {
        q: "How often should I inspect the undercarriage?",
        a: "Walk-around daily, measured inspection every 250 hours or monthly, and a full documented measurement (all four corners) every 1,000 hours or before big projects.",
      },
      {
        q: "What is the correct track sag for an excavator?",
        a: "Most excavators specify 20–30 mm of sag measured mid-span between carrier roller and idler — but always use your OEM figure. Measure on flat ground after rolling forward to settle the chain.",
      },
      {
        q: "What tools do I need?",
        a: "A tape measure, calipers or an ultrasonic gauge, a straightedge, and the wear-limit table for your model. Send VANROCK your model and we'll help you interpret your measurements at no cost.",
      },
    ],
    related: [
      { name: "All undercarriage parts", path: "/parts" },
      { name: "Track rollers by brand", path: "/parts/track-roller" },
      {
        name: "Track chain wear guide",
        path: "/guides/track-chain-wear-guide",
      },
    ],
  },
  "track-shoe-selection-guide": {
    title: "How to Choose Track Shoes: Width, Grouser Type & Material",
    metaTitle:
      "Track Shoe Selection Guide — Width, Grouser Type & When Wider Is Wrong",
    desc: "Choosing track shoe width and grouser type for excavators and dozers: ground pressure vs bending loads, single/double/triple grouser explained, and the costly mistake of over-wide shoes.",
    minutes: 6,
    sections: [
      {
        h: "The width trade-off nobody explains",
        ps: [
          "Wider shoes lower ground pressure — great for soft, wet terrain. But every extra centimetre of width increases the bending moment on the shoe, the load on the bolt joints, and the stress on chain links, rollers and idlers. On hard or rocky ground, over-wide shoes bend, crack and loosen; they are the most common self-inflicted undercarriage failure we see.",
          "Rule: fit the narrowest shoe that gives adequate flotation for your typical ground. Only fleets working exclusively in soft material should run the widest OEM option.",
        ],
      },
      {
        h: "Single, double or triple grouser?",
        ps: [
          "Triple grouser is standard on excavators: three lower bars share the load, allowing smooth turning with good all-round traction. Single grouser (tall bar) is the dozer standard — maximum penetration and pushing traction. Double grouser suits loaders and older dozers needing a balance of traction and manoeuvrability. Flat or rubber-padded shoes protect finished surfaces like asphalt.",
        ],
      },
      {
        h: "Material and hardening matter more than brand stickers",
        ps: [
          "A good shoe is rolled from boron steel and through-hardened, not just surface-hardened — so wear resistance stays constant as the grouser wears down. VANROCK shoes are pressed and hardened in our own factory to OEM hardness specs, with bolt holes drilled to exact chain pitch so the set torques up square first time.",
        ],
      },
      {
        h: "Ordering: what to tell your supplier",
        ps: [
          "Give machine model and serial, current shoe width, grouser style, and the number of shoes per side — or simply request a quote from your model page at VANROCK and we confirm the correct pattern for your chain. Always order new track bolts and nuts with shoes: reused bolts lose clamp force and are the top cause of loose shoes.",
        ],
      },
    ],
    faqs: [
      {
        q: "What width track shoes do I need?",
        a: "Match the OEM-approved options for your model, then pick the narrowest width that keeps you from sinking in typical ground. Wider than necessary accelerates bending failures and undercarriage wear.",
      },
      {
        q: "Can I mix new shoes with an old chain?",
        a: "Yes if chain pitch is within wear limits — the bolt pattern must match. If the chain is near its limit, a complete track group (chain + shoes factory-assembled) is often cheaper per hour of service life.",
      },
      {
        q: "How much do excavator track shoes cost?",
        a: "Depends on width and grouser type; full sets with hardware get volume pricing. Request a quote with your model — VANROCK replies with manufacturer prices.",
      },
    ],
    related: [
      { name: "Track shoes by brand", path: "/parts/track-shoe" },
      { name: "Track bolts & nuts", path: "/parts/track-bolt-nut" },
      { name: "Track groups (chain + shoes)", path: "/parts/track-group" },
    ],
  },
  "rubber-tracks-vs-steel-tracks": {
    title: "Rubber Tracks vs Steel Tracks: Which Should You Run?",
    metaTitle:
      "Rubber Tracks vs Steel Tracks — Costs, Lifespan & Which to Choose",
    desc: "Rubber tracks or steel tracks for your mini excavator or CTL? Compare lifespan, cost per hour, surface damage, traction and resale — plus hybrid options like bolt-on pads.",
    minutes: 5,
    sections: [
      {
        h: "The short answer",
        ps: [
          "Under ~8 tonnes and working around finished surfaces, kerbs and mixed urban sites: rubber tracks. Heavy demolition, quarry, forestry or constant rock: steel tracks. In between, the decision comes down to surface protection versus cut resistance — and how disciplined your operators are about pivot turns on concrete.",
        ],
      },
      {
        h: "Cost per hour, not sticker price",
        ps: [
          "A quality rubber track typically runs 1,200–2,000 hours; a steel undercarriage on the same machine can run 2,500–4,000 but costs more up front and transmits more vibration. Divide price by realistic hours for your ground: on abrasive rock rubber can drop under 800 hours and steel wins decisively; on landscaping and utility work rubber usually wins on both cost and versatility.",
        ],
      },
      {
        h: "What makes a rubber track survive",
        ps: [
          "Continuous (jointless) steel cords — a joined cord is a pre-made failure point; forged and through-hardened metal cores that grip the drive sprocket without rounding; and a cut-resistant outer compound. VANROCK rubber tracks are built exactly this way, sized per OEM (width × pitch × links, e.g. 300x52.5x84) for mini excavators, CTLs and dumpers across 350+ brands.",
        ],
      },
      {
        h: "Hybrids: pads and interchangeable systems",
        ps: [
          "Steel tracks with bolt-on rubber pads give surface protection with steel durability — a strong choice for machines that split time between demolition and finished surfaces. Some minis offer interchangeable steel/rubber undercarriages; if you own one, price both options from your model page before assuming.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long do rubber tracks last?",
        a: "Typically 1,200–2,000 hours. Avoiding sharp pivot turns on concrete, keeping correct tension, and clearing packed debris from the undercarriage are the three biggest life extenders.",
      },
      {
        q: "How do I find my rubber track size?",
        a: "It's moulded inside the track as width × pitch × link count (e.g. 300x52.5x84). Unreadable? Send VANROCK your machine model and serial — we confirm the size with the quote.",
      },
      {
        q: "Are aftermarket rubber tracks as good as OEM?",
        a: "Quality varies enormously. Judge by construction: jointless steel cords, forged cores, and compound quality. VANROCK tracks meet OEM specification and are 100% inspected — at manufacturer prices.",
      },
    ],
    related: [
      { name: "Rubber tracks by brand", path: "/parts/rubber-track" },
      { name: "Track groups", path: "/parts/track-group" },
      {
        name: "Undercarriage cost guide",
        path: "/guides/undercarriage-replacement-cost-guide",
      },
    ],
  },
  "undercarriage-replacement-cost-guide": {
    title: "Undercarriage Replacement Cost Guide: What You Should Pay",
    metaTitle:
      "Excavator Undercarriage Replacement Cost (2026) — Real Price Factors & How to Pay Less",
    desc: "What does undercarriage replacement really cost for an excavator or dozer? The price factors that matter, how the sourcing channel changes the price, and where fleets overpay.",
    minutes: 6,
    sections: [
      {
        h: "Why undercarriage is half your repair budget",
        ps: [
          "Industry studies consistently put undercarriage at 40–60% of a tracked machine's lifetime repair cost. A full undercarriage on a 20-tonne excavator — chains, shoes, rollers, idlers, sprockets, hardware — is a five-figure decision, so the sourcing channel you choose matters more here than anywhere else on the machine.",
        ],
      },
      {
        h: "The three price tiers explained",
        ps: [
          "OEM dealer parts carry the machine brand's logo and the highest price — often 60–100% above equivalent aftermarket, though frequently produced by the same undercarriage specialists. Aftermarket brands sold through importers and distributors price below OEM, but every layer in the chain adds its margin. Premium manufacturers that sell direct, like VANROCK, remove those layers entirely: the same OEM-specification steel, hardening and seals, priced by the people who actually make the part.",
        ],
      },
      {
        h: "What actually drives the price",
        ps: [
          "Machine weight class (a 30-tonne chain has 3× the steel of a 13-tonne one), sealed & lubricated vs dry chain construction, shoe width and grouser count, one-piece sprockets vs segment groups, and freight. Quantity matters too: complete undercarriage kits and per-side sets are always cheaper per part than piecemeal orders.",
        ],
      },
      {
        h: "Where fleets overpay (and how not to)",
        ps: [
          "The most common mistakes: replacing components one emergency at a time instead of planning from inspection data; paying OEM prices for wear parts where OEM adds no engineering difference; ignoring hardware (reused bolts loosen new shoes); and skipping freight consolidation. Get a complete-kit quote for your model — VANROCK prices any undercarriage combination for 9,000+ models, manufacturer-direct with worldwide shipping.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much does a full undercarriage cost for a 20-tonne excavator?",
        a: "Complete undercarriage kits vary widely by model and configuration — the honest answer is a same-day quote for your exact machine. Direct manufacturer pricing typically lands well below OEM dealer list for identical specification parts.",
      },
      {
        q: "Is aftermarket undercarriage as good as OEM?",
        a: "Specification-matched aftermarket from a real manufacturer is metallurgically equivalent — same boron/alloy steels, hardening depth and seal design. Ask for material and hardness specs; a factory that can't show them isn't a factory.",
      },
      {
        q: "When is repairing cheaper than replacing?",
        a: "Pin-and-bushing turns and partial component swaps make sense when the chain has internal life left. Above ~70% overall wear, complete replacement usually wins on cost per remaining hour.",
      },
    ],
    related: [
      { name: "All undercarriage parts", path: "/parts" },
      {
        name: "How VANROCK pricing works vs OEM & distributors",
        path: "/why-vanrock",
      },
      {
        name: "Undercarriage inspection checklist",
        path: "/guides/undercarriage-inspection-checklist",
      },
    ],
  },
  "extend-undercarriage-life": {
    title: "12 Ways to Extend Undercarriage Life (That Actually Work)",
    metaTitle:
      "How to Extend Excavator Undercarriage Life — 12 Proven Practices",
    desc: "Operator habits, maintenance settings and purchasing choices that measurably extend excavator and dozer undercarriage life — from track tension to travel discipline.",
    minutes: 5,
    sections: [
      {
        h: "Operating habits (free, and worth thousands)",
        ps: [
          "1. Minimise travel — undercarriage only wears when tracks turn; plan the site to dig more and drive less. 2. Slow down — wear rises roughly with the square of track speed. 3. Avoid pivot turns; make wide, gradual turns, alternating direction. 4. Travel with sprockets at the rear when possible — it lowers chain tension on the loaded side. 5. Don't work across slopes for long periods; flange and link side-wear soars.",
        ],
      },
      {
        h: "Maintenance settings",
        ps: [
          "6. Track tension is king: over-tensioned tracks can double internal pin/bushing loads. Check sag weekly and after ground-condition changes (mud packs and effectively over-tensions the chain). 7. Clean out packed mud and rock daily in winter — frozen packing destroys seals overnight. 8. Rotate wear: swap machine working direction on long linear jobs so both sides wear evenly.",
        ],
      },
      {
        h: "Inspection and timing",
        ps: [
          "9. Measure, don't eyeball — a 250-hour measurement routine turns failures into planned orders (use our 10-point checklist). 10. Replace in matched systems: a new chain on a hooked sprocket, or new shoes on stretched chains, wastes most of the new part's life.",
        ],
      },
      {
        h: "Buying choices",
        ps: [
          "11. Buy sealed & lubricated chains with induction-hardened pins and bushings, and through-hardened shoes — hardening depth is the difference between 2,000 and 4,000 hours. 12. Fit the narrowest shoe your ground allows. When it's time, VANROCK supplies premium OEM-spec components for 9,000+ models, sold direct — request a quote from any part page.",
        ],
      },
    ],
    faqs: [
      {
        q: "What single habit extends undercarriage life the most?",
        a: "Correct track tension, checked weekly. It's free and prevents the over-tension wear spiral that shortens chain life by 30–50%.",
      },
      {
        q: "Does washing the undercarriage help?",
        a: "Yes — daily cleanout in muddy or freezing conditions protects seals and prevents packing-induced over-tension. A shovel and 10 minutes beats a seized roller.",
      },
      {
        q: "Steel vs rubber for longevity?",
        a: "On abrasive ground, steel outlasts rubber 2:1 or more; on mixed urban work rubber can be more economical per hour. See our rubber vs steel tracks guide.",
      },
    ],
    related: [
      {
        name: "Undercarriage inspection checklist",
        path: "/guides/undercarriage-inspection-checklist",
      },
      {
        name: "Track chain wear guide",
        path: "/guides/track-chain-wear-guide",
      },
      { name: "All undercarriage parts", path: "/parts" },
    ],
  },
  "how-to-adjust-track-tension": {
    title: "How to Adjust Excavator Track Tension (Step by Step)",
    metaTitle:
      "How to Adjust Excavator Track Tension — Correct Sag, Step-by-Step & Common Mistakes",
    desc: "The correct track tension for excavators and dozers, how to measure sag, the step-by-step grease-adjuster procedure, and why over-tensioning is the #1 avoidable wear accelerator.",
    minutes: 6,
    sections: [
      {
        h: "What correct tension looks like",
        ps: [
          "Track tension is measured as sag: park on flat, firm ground, roll forward a machine length to settle the chain, and measure the vertical distance between the track and a straightedge laid across the top of the chain between the carrier roller and idler. Most excavators specify 20–30 mm of sag; dozers and rubber-tracked minis have their own figures — always confirm in your operator's manual.",
          "Ground matters: in packing conditions (mud, clay, snow) run the track slightly looser, because packed material effectively over-tensions the chain. On clean, hard ground you can run closer to the tight end of the spec.",
        ],
      },
      {
        h: "Step-by-step adjustment (grease-type adjuster)",
        ps: [
          "1. Park on level ground, travel forward slowly and stop without using the brakes hard. 2. Lay a straightedge over the chain between carrier roller and idler and measure sag at the lowest point. 3. To tighten: pump grease into the track adjuster valve (behind the small cover on the track frame) with a grease gun until sag reaches spec. 4. To loosen: crack the valve open SLOWLY no more than one turn and let grease escape — never remove the fitting fully, the recoil spring is under enormous load. 5. Travel back and forth, re-measure, and repeat on the other side.",
        ],
      },
      {
        h: "The mistakes that destroy undercarriages",
        ps: [
          "Over-tensioning is the most expensive mistake in undercarriage care: an over-tight chain can double the internal loads on pins, bushings, idler and rollers, cutting component life by a third or more. Second is ignoring ground changes — a chain tensioned on dry ground becomes rock-tight after a day in packing clay. Third is loosening the valve carelessly: escaping grease under pressure is dangerous. If the adjuster will not hold tension, the seal has failed — replace the adjuster cartridge, not just the grease.",
        ],
      },
      {
        h: "When tension won't fix it",
        ps: [
          "If the track keeps going slack shortly after adjustment, look deeper: a leaking adjuster seal, a collapsed recoil spring, or a chain past its wear limit that has physically elongated. Measure chain pitch (see our track chain wear guide) — a stretched chain cannot be tensioned back into spec and will keep de-tracking until replaced.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much sag should an excavator track have?",
        a: "Most excavators specify 20–30 mm of vertical sag measured mid-span between the carrier roller and idler on flat ground. Rubber-tracked mini excavators typically specify sag at the middle of the track span — check your operator's manual for the exact figure.",
      },
      {
        q: "What happens if tracks are too tight?",
        a: "Internal loads on pins, bushings, rollers and the idler rise sharply — wear accelerates on every component, fuel consumption increases, and the risk of snapping components in cold weather rises. Over-tension is worse than slightly loose.",
      },
      {
        q: "Why does my track keep getting loose?",
        a: "Usually a leaking track adjuster seal or a worn-out (elongated) chain. If pumping grease no longer holds tension, replace the adjuster; if the chain is past its pitch wear limit, it needs replacement.",
      },
    ],
    related: [
      {
        name: "Track chain wear guide",
        path: "/guides/track-chain-wear-guide",
      },
      {
        name: "Why tracks come off",
        path: "/guides/thrown-track-causes-prevention",
      },
      { name: "Idlers & tension assemblies", path: "/parts/idler" },
    ],
  },
  "thrown-track-causes-prevention": {
    title: "Why Excavator Tracks Come Off — Causes, Fixes & Prevention",
    metaTitle:
      "Excavator Threw a Track? Causes, How to Fix It & How to Prevent De-Tracking",
    desc: "Why excavator and dozer tracks come off (de-tracking), how to get a thrown track back on, and the tension, alignment and operating fixes that stop it happening again.",
    minutes: 6,
    sections: [
      {
        h: "The five real causes of a thrown track",
        ps: [
          "1. Loose track tension — the #1 cause by far; a slack chain lifts off the idler in a turn and walks over the flanges. 2. Worn components — a stretched chain, worn idler flanges, worn roller flanges and hooked sprocket teeth all reduce the guiding depth that keeps the chain on. 3. Misalignment — a bent track frame, worn idler guide plates or incorrectly shimmed idler lets the chain run at an angle. 4. Aggressive operation — fast spot turns, turning against a kerb or slope, working across steep side slopes. 5. Debris packing — rock or frozen mud packed between sprocket and chain physically levers the track off.",
        ],
      },
      {
        h: "Getting a thrown track back on",
        ps: [
          "For a partially thrown track (off the idler or rollers but still on the sprocket): release all track tension via the adjuster valve, use the bucket/blade to lift that side, and use travel motion plus a pry bar to walk the chain back over the idler — then re-tension. For a fully thrown track, you'll typically need to break the chain at the master link or master shoe, lay it out flat, drive on, and reconnect. On rubber-tracked minis the same principle applies but by hand: tension fully released, lift the machine, work the track over the idler with a bar.",
          "Safety first: never put hands between chain and rollers, use certified lifting points, and treat the pressurised adjuster with respect.",
        ],
      },
      {
        h: "Prevention checklist",
        ps: [
          "Check tension weekly and after ground changes. Replace idler and roller flanges before they wear round — flanges are what guide the chain. Inspect idler guide plates/shims and keep them adjusted. Clean packed material daily in winter and clay. Coach operators: wide turns, low speed on turns, no spot-turning against obstacles, and avoid prolonged side-slope travel. If one machine keeps throwing the same track, measure frame alignment — a bent frame will de-track new parts too.",
        ],
      },
    ],
    faqs: [
      {
        q: "How do I put a track back on a mini excavator?",
        a: "Release track tension fully at the grease valve, lift that side with the blade or boom, feed the rubber track over the sprocket first, then work it over the idler with a pry bar while slowly rotating the drive. Re-tension to spec afterwards.",
      },
      {
        q: "Why does my excavator keep throwing tracks on one side?",
        a: "Persistent one-sided de-tracking points to worn idler/roller flanges, a failed idler guide, chain past its wear limit, or a bent track frame on that side. Measure the components — re-tensioning alone won't cure a mechanical cause.",
      },
      {
        q: "Is a thrown track dangerous for the machine?",
        a: "Repeated de-tracking damages link rails, roller flanges and the idler, and can cut hydraulic hoses on the frame. Fix the root cause quickly — every thrown track accelerates the wear that caused it.",
      },
    ],
    related: [
      {
        name: "Track tension guide",
        path: "/guides/how-to-adjust-track-tension",
      },
      {
        name: "Track alignment problems",
        path: "/guides/track-alignment-problems",
      },
      { name: "Idlers by brand", path: "/parts/idler" },
    ],
  },
  "track-alignment-problems": {
    title: "Track Alignment Problems: Machine Pulls, Snaking & Uneven Wear",
    metaTitle:
      "Excavator Track Alignment Problems — Pulling to One Side, Snaking Chain & Flange Wear",
    desc: "Diagnosing track alignment issues on excavators and dozers: why the machine pulls to one side, why the chain snakes, what one-sided flange wear means, and how to correct it.",
    minutes: 5,
    sections: [
      {
        h: "Reading the symptoms",
        ps: [
          "Machine pulls or steers to one side under equal drive: usually a drive issue (travel motor, brake) — but if drive is healthy, compare chain wear side to side; a badly stretched chain on one side changes the effective rolling circumference. Chain visibly snakes over the top run: loose tension or worn carrier roller. Shiny, angled wear on one side of roller and idler flanges: the chain is running at an angle — an alignment problem.",
        ],
      },
      {
        h: "The usual mechanical causes",
        ps: [
          "Worn or missing idler guide plates and shims let the idler cock sideways in the frame — the chain then feeds into the rollers at an angle, wearing flanges on one side. Roller flanges worn round lose their guiding ability. On dozers, misadjusted or worn track frame pivots and diagonal braces do the same at the frame level. Finally, a physically bent track frame (impact damage) will misalign everything on that side no matter how new the parts are.",
        ],
      },
      {
        h: "How to check and correct",
        ps: [
          "With the track raised, sight down the chain line from idler to sprocket — the links should run parallel to the frame. Measure the gap between chain and roller flanges on both sides at several points. Check idler guide plate clearance against your OEM spec (typically 0.5–1.0 mm per side) and shim as needed. Replace flanged components worn beyond limit. If alignment cannot be corrected with guides and shims, have the frame checked for straightness before spending on new parts.",
        ],
      },
    ],
    faqs: [
      {
        q: "Why does my excavator chain wear on one side?",
        a: "One-sided flange and link wear means the chain runs at an angle — usually worn idler guide plates, cocked idler, worn roller flanges, or a bent frame. Correct the alignment first, or new parts will wear the same way.",
      },
      {
        q: "What are idler guide plates and when do they need replacing?",
        a: "They're the wear plates that keep the idler square in the track frame as it slides under recoil. When their clearance exceeds the OEM limit, the idler tilts and misfeeds the chain — replace or shim them at every chain replacement.",
      },
      {
        q: "Can misalignment throw a track?",
        a: "Yes — an angled chain climbs the idler or roller flanges in turns, which is one of the main de-tracking causes alongside loose tension.",
      },
    ],
    related: [
      {
        name: "Why tracks come off",
        path: "/guides/thrown-track-causes-prevention",
      },
      { name: "Track rollers by brand", path: "/parts/track-roller" },
      { name: "Idlers by brand", path: "/parts/idler" },
    ],
  },
  "pin-and-bushing-turn": {
    title: "Pin & Bushing Turn: What It Is and When It's Worth It",
    metaTitle:
      "Pin and Bushing Turn Explained — Cost vs New Track Chain, When It Makes Sense",
    desc: "What a pin and bushing turn is, how it restores track chain pitch, what it costs versus a new chain, and the cases where turning is — and isn't — worth the money.",
    minutes: 5,
    sections: [
      {
        h: "What a pin and bushing turn actually does",
        ps: [
          "Bushings wear on one side: externally where the sprocket engages, and internally against the pin — both on the drive side. A 'turn' presses the chain apart, rotates pins and bushings 180°, and reassembles it so the unworn side takes the load. Done at the right time, it can restore most of the chain's remaining structural life and returns the pitch close to nominal, protecting the sprocket.",
        ],
      },
      {
        h: "The timing window",
        ps: [
          "Turning only makes sense inside a window: after external bushing wear reaches roughly its limit but BEFORE internal wear (pitch stretch) or link rail wear consume the chain. Turn too late and the pitch is already gone — the chain will still hammer the sprocket. As a rule, a turn is considered around 100% external bushing wear with link height still under ~50% worn. Dry chains are the classic candidates; sealed and lubricated chains often reach link and shoe limits first, making a turn less useful.",
        ],
      },
      {
        h: "Turn vs new chain: the honest math",
        ps: [
          "A press-shop turn typically costs 40–60% of a new chain once labour, transport and downtime are counted — and the turned chain is not new: seals are disturbed, and remaining life is usually 60–80% of a fresh chain. With today's manufacturer-direct chain pricing, many fleets find a new sealed & lubricated chain the better cost-per-hour, especially for machines far from a press shop. Turning still wins for large dozers with expensive chains, fleets with in-house press capacity, and remote operations where a chain is easier to service than to freight.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much does a pin and bushing turn cost?",
        a: "Typically 40–60% of a new chain including press labour and downtime, varying by region and chain size. Get both numbers before deciding — request a new-chain quote for your model and compare cost per remaining hour.",
      },
      {
        q: "Can sealed and lubricated chains be turned?",
        a: "Physically yes, but the turn destroys the factory seals and the lubricant advantage, so remaining internal life drops sharply. SALT chains are usually run to their limits and replaced instead.",
      },
      {
        q: "How do I know if my chain is still turnable?",
        a: "Measure external bushing OD, pitch elongation and link height. Turnable = bushings at/near external limit while pitch and link height retain meaningful life. Send us your measurements — we'll tell you honestly whether a turn or a new chain wins.",
      },
    ],
    related: [
      {
        name: "Track chain wear guide",
        path: "/guides/track-chain-wear-guide",
      },
      { name: "Track chains by brand", path: "/parts/track-chain" },
      {
        name: "Undercarriage cost guide",
        path: "/guides/undercarriage-replacement-cost-guide",
      },
    ],
  },
  "undercarriage-wear-chart-measurement": {
    title: "Undercarriage Wear Measurement: Tools, Method & % Wear",
    metaTitle:
      "How to Measure Undercarriage Wear — Tools, Wear Charts & Percent Worn Explained",
    desc: "How to measure undercarriage wear like a pro: the tools (calipers, depth gauge, ultrasonic), what to measure on each component, and how wear charts convert readings to percent worn.",
    minutes: 6,
    sections: [
      {
        h: "The toolkit",
        ps: [
          "You need less than you think: a steel tape, a straightedge, outside calipers (or a large vernier), a depth gauge, and optionally an ultrasonic wear gauge that reads metal thickness through the component — the fastest tool for rollers and idlers because the machine doesn't need lifting. Add the OEM wear-limit chart for your model (we can supply the relevant figures with any quote) and a simple recording sheet per corner of the machine.",
        ],
      },
      {
        h: "What to measure, component by component",
        ps: [
          "Track chain: pitch over 4–5 links (tape), link height (depth gauge from shoe face or straightedge), external bushing OD (calipers). Rollers and idlers: tread diameter (calipers) or shell thickness (ultrasonic) at the deepest-worn point. Sprocket: visual profile against a new segment's silhouette — hooked or sharpened teeth are the tell. Shoes: grouser height from the shoe plate. Hardware: check bolt torque and elongated bolt holes.",
        ],
      },
      {
        h: "Understanding percent worn",
        ps: [
          "Every component has a new dimension and a 100%-worn dimension published by the OEM; percent worn is your reading placed proportionally between them. The critical insight: wear is not linear. Hardened surfaces wear slowly until the case depth is consumed — past 100%, wear accelerates 3–5× through the soft core, and a '110% worn' roller can destroy itself and the chain rail in a few hundred hours. That's why professionals replace at 100–120%, never 'when it fails'.",
        ],
      },
      {
        h: "Turning numbers into decisions",
        ps: [
          "Record percent worn per component per side, every 250–500 hours. The trend line tells you the wear rate in %-per-100-hours — project it forward to plan replacements at service windows instead of failures. Components above 80% go on the order list; anything accelerating abnormally (one roller wearing 2× its neighbours) gets investigated for seizure or misalignment.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is an ultrasonic undercarriage gauge?",
        a: "A handheld tool that measures remaining metal thickness through the surface using ultrasound — you get roller, idler and link readings in seconds without lifting the machine. Popular with dealers and fleet inspectors; calipers achieve the same on a raised track.",
      },
      {
        q: "Where do I find wear limits for my machine?",
        a: "OEM service literature publishes new/worn dimensions per component. Tell us your machine model — VANROCK supplies the relevant wear-limit figures together with part quotes at no cost.",
      },
      {
        q: "How accurate does measuring need to be?",
        a: "Within a few tenths of a millimetre is fine — the goal is a reliable trend. Always measure at the same points (deepest wear, marked positions) so readings are comparable over time.",
      },
    ],
    related: [
      {
        name: "10-point inspection checklist",
        path: "/guides/undercarriage-inspection-checklist",
      },
      {
        name: "Track chain wear guide",
        path: "/guides/track-chain-wear-guide",
      },
      { name: "All undercarriage parts", path: "/parts" },
    ],
  },
  "mini-excavator-undercarriage-guide": {
    title:
      "Mini Excavator Undercarriage Guide: Tracks, Rollers & Common Failures",
    metaTitle:
      "Mini Excavator Undercarriage Guide — Rubber Track Sizes, Rollers, Idlers & Common Failures",
    desc: "Everything mini excavator owners ask: how to read rubber track sizes, when to replace tracks, why small rollers and idlers fail early, and how to keep a compact undercarriage cheap to run.",
    minutes: 6,
    sections: [
      {
        h: "Minis wear differently",
        ps: [
          "Compact machines live a harder undercarriage life than their big brothers: constant travel between tasks, trailer loading several times a day, kerbs, concrete and tight pivot turns. Rubber components add their own failure modes — cuts, de-lugging and cord corrosion — that steel undercarriages never see. The result: undercarriage is proportionally the biggest running cost on most minis.",
        ],
      },
      {
        h: "Reading rubber track sizes",
        ps: [
          "Every rubber track is sized as width × pitch × link count, moulded on the inside — e.g. 300x52.5x84 means 300 mm wide, 52.5 mm pitch, 84 links. All three must match your machine; pitch and link count must also match your sprocket. If the marking is unreadable, count the links and measure width and pitch, or simply send us your machine model and serial — we confirm the size with every quote.",
        ],
      },
      {
        h: "The classic failure points",
        ps: [
          "Rubber tracks: cut lugs and exposed steel cords (rock, demolition debris), de-lugging from spot turns on concrete, and cord snap from over-tension. Bottom rollers on minis are small and low to the ground — they seize from mud packing and die quietly; a seized roller flat-spots in days. Idlers and tension springs suffer from the same packing. Sprockets round off quickly once a stretched or wrong-pitch track runs over them.",
        ],
      },
      {
        h: "Keeping a mini cheap to run",
        ps: [
          "Wash the undercarriage out at the end of muddy days — ten minutes that doubles roller life. Keep tension at spec (10–15% sag for rubber tracks; check your manual): tight tracks snap cords and strip lugs. Train operators out of pivot turns on hard surfaces. Replace tracks in pairs when both are worn — mismatched circumference makes the machine crab. And price parts before assuming minis are cheap at the dealer: VANROCK stocks rubber tracks, rollers, idlers and sprockets for all major mini brands at manufacturer pricing.",
        ],
      },
    ],
    faqs: [
      {
        q: "How do I know what size rubber track I need?",
        a: "Read the size moulded inside your current track (width × pitch × links, e.g. 300x52.5x84). Unreadable? Send us the machine model and serial number — we confirm the exact size and quote.",
      },
      {
        q: "When should mini excavator tracks be replaced?",
        a: "When lugs are worn flat, steel cords are visible through cuts, lugs are tearing off, or the track has stretched so it can no longer hold tension. Cracked rubber alone isn't terminal — exposed or corroded cord is.",
      },
      {
        q: "Why do my mini's rollers keep failing?",
        a: "Small rollers sit low in the mud — packing starves and seizes them. Daily cleanout, correct tension, and replacing seized rollers immediately (before they flat-spot and damage the track) are the fixes.",
      },
    ],
    related: [
      { name: "Rubber tracks by brand", path: "/parts/rubber-track" },
      {
        name: "Rubber vs steel tracks",
        path: "/guides/rubber-tracks-vs-steel-tracks",
      },
      { name: "Track rollers by brand", path: "/parts/track-roller" },
    ],
  },
  "used-excavator-undercarriage-check": {
    title: "Buying a Used Excavator? How to Check the Undercarriage",
    metaTitle:
      "How to Inspect the Undercarriage When Buying a Used Excavator — 15-Minute Check",
    desc: "The undercarriage is up to 50% of a used excavator's repair bill. A 15-minute inspection routine — what to measure, the seller tricks to spot, and how to price wear into your offer.",
    minutes: 6,
    sections: [
      {
        h: "Why the undercarriage decides the deal",
        ps: [
          "Two identical-looking 20-tonne excavators can differ by the price of a car in undercarriage condition alone. A machine at 80% undercarriage wear needs a five-figure spend within a year; the same model at 30% has thousands of hours left. Yet most buyers spend their inspection time on the engine and hydraulics — sellers know it.",
        ],
      },
      {
        h: "The 15-minute routine",
        ps: [
          "Walk both sides with a tape and calipers. Measure chain pitch over 4 links against nominal, link height, and bushing OD at the shiniest point. Spin-check carrier rollers, look for oil weep at every roller and the idlers, and check for flat-spotted or scalloped roller treads. Sight the sprocket teeth against a photo of a new one — sharpened or hooked teeth mean chain AND sprocket money. Check shoe grouser height and look for welded-up repairs, cracked shoes and mismatched hardware. Finally sight down each chain line for alignment and check the frame for impact damage and bent guards.",
        ],
      },
      {
        h: "Seller tricks to spot",
        ps: [
          "Fresh paint on rails and rollers hides wear measurement points — insist on measuring, paint tells you nothing. Over-tensioned tracks make a stretched chain look tight; measure pitch, not sag. A single new component (one new idler, a few new shoes) often marks a recent failure, not generosity. And 'we just serviced the undercarriage' should come with measurement records — no records, assume the worst.",
        ],
      },
      {
        h: "Pricing wear into your offer",
        ps: [
          "Convert your measurements to rough percent worn, then get a real quote for what's within a year of replacement — chains, sprockets, and any component over ~70%. Deduct it. VANROCK prices a complete undercarriage for any of 9,000+ models, which makes a powerful negotiation document to bring to the seller.",
        ],
      },
    ],
    faqs: [
      {
        q: "What percent undercarriage wear is acceptable when buying used?",
        a: "Under 50% is comfortable; 50–75% means budget for major parts within a year; over 75% should be fully priced into the offer using a real replacement quote for that model.",
      },
      {
        q: "Can I judge the undercarriage by hours on the meter?",
        a: "No — application matters more than hours. 3,000 gentle landscaping hours can leave less wear than 1,500 demolition hours. Always measure; never infer from the hour meter.",
      },
      {
        q: "Which undercarriage part is most expensive to ignore?",
        a: "The chain–sprocket pair. A stretched chain destroys a sprocket and vice versa, so discovering one worn usually means buying both. Check those two first on any used machine.",
      },
    ],
    related: [
      {
        name: "Wear measurement tools & charts",
        path: "/guides/undercarriage-wear-chart-measurement",
      },
      {
        name: "Undercarriage cost guide",
        path: "/guides/undercarriage-replacement-cost-guide",
      },
      { name: "Request a quote for any model", path: "/request-quote" },
    ],
  },
  "undercarriage-maintenance-schedule": {
    title: "Undercarriage Maintenance Schedule: Daily to 2,000 Hours",
    metaTitle:
      "Excavator Undercarriage Maintenance Schedule — Daily, Weekly, 250h and 1,000h Tasks",
    desc: "A complete undercarriage maintenance schedule for excavators and dozers: daily walkarounds, weekly tension checks, 250-hour measurements and the 1,000–2,000 hour planning cycle.",
    minutes: 5,
    sections: [
      {
        h: "Daily (5 minutes)",
        ps: [
          "Walk both sides before start-up: look for oil weep at rollers and idlers, loose or missing shoe bolts, cut or de-lugged rubber tracks, rock jammed between sprocket and chain, and anything hanging or bent. Clean out packed mud, clay and stones at the end of the shift — packing is the silent killer of seals and the cause of overnight freeze damage in winter.",
        ],
      },
      {
        h: "Weekly (15 minutes)",
        ps: [
          "Measure track sag on both sides and adjust to spec for current ground conditions. Spin-check carrier rollers. Torque-check a sample of shoe bolts (all of them after any shoe work — new hardware settles). Grease anything on the undercarriage your lube chart lists (recoil/adjuster area on some machines). Note anything trending: a roller weeping this week fails next month.",
        ],
      },
      {
        h: "Every 250–500 hours (30 minutes)",
        ps: [
          "The measured inspection: chain pitch, link height, bushing OD, roller and idler treads, sprocket profile, grouser height — recorded per side against your last readings (use our 10-point checklist and wear measurement guide). Update the percent-worn table and the wear-rate trend. Order anything projected to hit 100% before your next planned service window.",
        ],
      },
      {
        h: "1,000–2,000 hours: the planning cycle",
        ps: [
          "Review the full wear table and decide the strategy for the next 1,000 hours: single components now, a matched chain+sprocket change, or a complete undercarriage at the next major downtime. Bundle orders — complete kits and per-side sets are cheaper per part and one freight. A one-day quote for your exact model turns this from guesswork into a budget line.",
        ],
      },
    ],
    faqs: [
      {
        q: "How often should track tension be checked?",
        a: "Weekly as a baseline, plus every time ground conditions change significantly (wet to dry, clean to packing). It's a two-minute check that protects every other component.",
      },
      {
        q: "What undercarriage maintenance does a rubber-tracked machine need?",
        a: "The same rhythm: daily debris cleanout and damage check, weekly tension, periodic measurement of lug height and inspection for exposed cords — plus extra attention after work on demolition debris or sharp rock.",
      },
      {
        q: "Is undercarriage maintenance really worth the time?",
        a: "Undercarriage is 40–60% of lifetime repair cost on tracked machines. Fleets that run measured inspections routinely report 20–30% lower undercarriage cost per hour than run-to-failure operations.",
      },
    ],
    related: [
      {
        name: "10-point inspection checklist",
        path: "/guides/undercarriage-inspection-checklist",
      },
      {
        name: "Track tension guide",
        path: "/guides/how-to-adjust-track-tension",
      },
      {
        name: "12 ways to extend undercarriage life",
        path: "/guides/extend-undercarriage-life",
      },
    ],
  },
  "sprocket-wear-guide": {
    title: "Sprocket Wear: Signs, Causes & When to Replace",
    metaTitle:
      "Excavator & Dozer Sprocket Wear — Hooked Teeth, Causes & Replacement Timing",
    desc: "How to recognise sprocket wear on excavators and dozers: hooked and sharpened teeth, what causes fast sprocket wear, and why the chain–sprocket pair must be replaced together.",
    minutes: 5,
    sections: [
      {
        h: "What worn sprocket teeth look like",
        ps: [
          "A healthy sprocket tooth is thick with a flat-ish tip and symmetric flanks. Wear shows first as polishing on the drive flank, then as thinning — teeth that look 'sharpened' — and finally as hooking, where the tip curls in the drive direction. A quick field test: hold a new segment (or a printed profile) against a tooth; daylight along the drive flank tells the story. Teeth you could 'shave with' are 100% worn.",
        ],
      },
      {
        h: "Why sprockets wear fast — it's usually the chain",
        ps: [
          "Sprockets and bushings wear as a pair. When chain pitch stretches, every bushing lands slightly wrong on its tooth and slides under full drive torque — grinding both. That's why a sprocket that lasted 4,000 hours dies in 1,500 running a stretched chain, and why reverse travel under load (which drives through fewer teeth at higher force) accelerates it. Abrasive material packed in the sprocket area and over-tension finish the job.",
        ],
      },
      {
        h: "Replace together, or pay twice",
        ps: [
          "Fitting a new chain on hooked sprockets destroys the new bushings within a few hundred hours — the worn tooth profile hammers exactly where the bushing is hardest-worked. The rule is simple: new chain, new sprocket (or segment group), always. The reverse is more forgiving but still poor practice. On dozers with bolt-on segment groups, segments can be changed without breaking the chain — a genuine mid-life option when the chain still measures healthy.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long does a sprocket last?",
        a: "Matched with a healthy chain: typically the life of the chain, 2,000–4,000 hours. Running a stretched chain or heavy reverse-loaded work can halve it. Always assess sprocket and chain together.",
      },
      {
        q: "Can I replace just one sprocket segment?",
        a: "You can bolt on a single segment, but mixed wear across segments engages the chain unevenly — replace segments as a full set per side for even load sharing.",
      },
      {
        q: "How much does a new sprocket cost?",
        a: "It varies by machine size and one-piece vs segmented design. Request a quote for your model — manufacturer pricing, and we'll advise if a chain+sprocket package makes sense based on your measurements.",
      },
    ],
    related: [
      { name: "Sprockets & segments by brand", path: "/parts/sprocket" },
      {
        name: "Track chain wear guide",
        path: "/guides/track-chain-wear-guide",
      },
      {
        name: "Wear measurement tools",
        path: "/guides/undercarriage-wear-chart-measurement",
      },
    ],
  },
  "dozer-vs-excavator-undercarriage": {
    title: "Dozer vs Excavator Undercarriage: What's Actually Different",
    metaTitle:
      "Dozer vs Excavator Undercarriage — Component Differences, Wear Patterns & Parts Buying",
    desc: "Dozer and excavator undercarriages share the concept but differ in chains, sprockets, rollers and wear patterns. What's different, why it matters, and what it means when buying parts.",
    minutes: 5,
    sections: [
      {
        h: "Same idea, different duty",
        ps: [
          "An excavator undercarriage exists mostly to reposition the machine — short travel, lots of standing digging. A dozer undercarriage IS the machine: it transmits full engine power into the ground all day. That difference drives every design choice: dozer components are heavier, serviceable in the field, and engineered for continuous traction load rather than intermittent travel.",
        ],
      },
      {
        h: "Component differences that matter",
        ps: [
          "Chains: excavators standardise on sealed & lubricated chains; dozers use SALT or heavy greased chains, often with master shoes for field splitting, and are the classic candidates for pin-and-bushing turns. Sprockets: excavators run one-piece bolt-on sprockets; dozers run segmented rims (segment groups) replaceable without breaking the chain — and elevated-sprocket designs add their own geometry. Shoes: dozers use single-grouser for traction; excavators triple-grouser for smooth turning. Rollers and idlers are heavier on dozers, with recoil and equaliser hardware taking constant push load.",
        ],
      },
      {
        h: "Wear patterns and buying implications",
        ps: [
          "Dozers wear bushings and sprocket segments fastest (drive torque), and side-slope work chews flanges; excavators wear rollers and links from travel and packing. Practically: dozer owners should track bushing OD and consider turns; excavator owners should watch pitch and roller treads. When ordering, dozer parts need the machine's serial AND shoe/segment configuration — same model can run several setups. VANROCK catalogues both machine families across 350+ brands, from mini excavators to large dozers.",
        ],
      },
    ],
    faqs: [
      {
        q: "Are dozer and excavator undercarriage parts interchangeable?",
        a: "Almost never — even where dimensions look close, chains, sprockets and rollers are engineered for different load profiles. Always order by machine model and serial number.",
      },
      {
        q: "Why do dozer undercarriages cost more to maintain?",
        a: "They transmit full drive power continuously, so bushing, sprocket and shoe wear runs faster per hour than an excavator doing the same site's digging. Measured maintenance narrows the gap significantly.",
      },
      {
        q: "What is an elevated sprocket undercarriage?",
        a: "A dozer design that raises the drive sprocket above the ground line, isolating it from impact and packing, with bogey-style rollers underneath. It changes component geometry — parts are model-specific, so quote by serial number.",
      },
    ],
    related: [
      { name: "Track groups (chain + shoes)", path: "/parts/track-group" },
      {
        name: "Pin & bushing turn guide",
        path: "/guides/pin-and-bushing-turn",
      },
      { name: "Track shoes by brand", path: "/parts/track-shoe" },
    ],
  },
  "winter-undercarriage-care": {
    title: "Winter Undercarriage Care: Mud, Freeze & Cold-Weather Damage",
    metaTitle:
      "Winter Undercarriage Care — Frozen Tracks, Packing Damage & Cold Weather Checklist",
    desc: "How freezing weather destroys undercarriages: frozen packing, brittle impact damage, seized rollers — and the end-of-shift routine that prevents the classic winter failures.",
    minutes: 5,
    sections: [
      {
        h: "What winter actually does to an undercarriage",
        ps: [
          "The killer is packing plus freeze. Wet clay and snow pack between rollers, chain and sprocket during the shift, then freeze overnight into concrete. Next morning's first travel forces rotating components through solid ice: seals tear, rollers seize, lugs strip off rubber tracks, and an over-packed chain runs effectively over-tensioned — multiplying loads on pins and bushings. Add cold-brittle steel (impact toughness drops with temperature) and hard-frozen ground that behaves like rock, and winter becomes the highest-wear season for most fleets.",
        ],
      },
      {
        h: "The end-of-shift routine that saves thousands",
        ps: [
          "Ten minutes before parking: travel a few machine lengths back and forth on clean ground to sling out loose material, then clear packed mud from rollers, idler pockets and the sprocket with a shovel or pressure washer. Park on planks, gravel or mats — not in standing water or mud that will freeze around the tracks. Where possible leave the machine with tracks slightly LOOSER than summer spec: frozen packing will take up the slack.",
        ],
      },
      {
        h: "Cold-morning start discipline",
        ps: [
          "If the tracks are frozen down, don't force travel — break the ice around the chain first; drive motors can generate enough torque to strip a rubber track or damage a final drive against frozen tracks. Move off slowly, short distances, letting components free up before working speed. Re-check tension after an hour — apparent morning tension drops once ice clears.",
        ],
      },
    ],
    faqs: [
      {
        q: "Should track tension be different in winter?",
        a: "Run to spec but check far more often, and bias to the looser end in packing conditions — frozen material effectively tightens the track. Never leave a machine parked tight in freezing mud.",
      },
      {
        q: "My tracks are frozen to the ground — what do I do?",
        a: "Break the ice contact around both tracks mechanically (bar, bucket assist) before travelling. Forcing frozen tracks with the drive motors strips rubber tracks and can damage sprockets and final drives.",
      },
      {
        q: "Do rubber tracks handle cold badly?",
        a: "Quality compounds stay serviceable well below freezing, but cold rubber cuts more easily and stripped lugs from frozen-down starts are the classic winter failure. Gentle first movements and clean parking spots prevent most of it.",
      },
    ],
    related: [
      {
        name: "Maintenance schedule",
        path: "/guides/undercarriage-maintenance-schedule",
      },
      {
        name: "12 ways to extend undercarriage life",
        path: "/guides/extend-undercarriage-life",
      },
      { name: "Rubber tracks by brand", path: "/parts/rubber-track" },
    ],
  },
};

export const GUIDE_ORDER = [
  "track-chain-wear-guide",
  "how-to-adjust-track-tension",
  "thrown-track-causes-prevention",
  "undercarriage-inspection-checklist",
  "undercarriage-wear-chart-measurement",
  "track-alignment-problems",
  "pin-and-bushing-turn",
  "sprocket-wear-guide",
  "track-shoe-selection-guide",
  "rubber-tracks-vs-steel-tracks",
  "mini-excavator-undercarriage-guide",
  "dozer-vs-excavator-undercarriage",
  "undercarriage-replacement-cost-guide",
  "used-excavator-undercarriage-check",
  "undercarriage-maintenance-schedule",
  "winter-undercarriage-care",
  "extend-undercarriage-life",
];

export const POPULAR_SEARCHES = [
  { label: "CAT 320 track chain", path: "/brand/caterpillar/320-v1" },
  {
    label: "Komatsu PC200 undercarriage parts",
    path: "/brand/komatsu/pc200-5",
  },
  { label: "Hitachi ZX200 track rollers", path: "/brand/hitachi/zx200" },
  { label: "Kobelco SK210 track chain", path: "/brand/kobelco/sk210-8" },
  { label: "Volvo EC210 undercarriage", path: "/brand/volvo/ec210b-lc" },
  { label: "Bobcat T190 rubber track", path: "/brand/bob-cat/t190" },
  {
    label: "Caterpillar track chains price",
    path: "/parts/track-chain/caterpillar",
  },
  { label: "Komatsu track shoes", path: "/parts/track-shoe/komatsu" },
  { label: "Hitachi front idlers", path: "/parts/idler/hitachi" },
  { label: "Doosan DX140 track roller", path: "/brand/doosan/dx140lc" },
  { label: "Hyundai R210 track chain", path: "/brand/hyundai/r210lc-3" },
  { label: "Takeuchi TB015 rubber track", path: "/brand/takeuchi/tb015" },
  { label: "JCB JS110 undercarriage", path: "/brand/jcb/js110" },
  { label: "Yanmar VIO20 rubber track", path: "/brand/yanmar/vio20" },
  { label: "Kubota KX021 track roller", path: "/brand/kubota/kx021" },
  { label: "Excavator track chain price", path: "/parts/track-chain" },
];
